// All kinds of stuff to create a layout from a config json
// main function: createConfigLayout(config, options = null, result = null)

import {
  MAX_CONTROLS,
  WIRE_SPACE,
  SOLAR_COLLECTOR_TYPES,
  configuratorComponentInfo,
  circuitComponentInfo,
  aliasMap,
  doubleControlboxComponentInfo,
} from './config-item-info';

import {
  findSolarKitProductData,
  getSystemProjectSet,
} from './project-definition-data-access';

import {
  getProductInfoBySlotName,
} from './component';

import { getChimneyLayout } from './component/chimney/index';

import { CIRCUIT_TYPES_BY_NAME } from './circuit-types';

const circuitDisplayInfo = circuitComponentInfo;

const CONTROLBOX_CENTER_Y = 480;

const CIRCUIT_CENTER_Y = 700;
const CONTROL_CENTER_Y = 850;

// Distance between wires connected to a mixed circuit
const MIXED_CIRCUIT_WIRE_DISTANCE = 68;
/*
(Mixed circuits look like this:)
  _   _   _
 | | | | | |
 | |_| |_| |
 |         |
 |         |

*/

const SOLAR_COLLECTOR_Y = 820;

const WALL_Y = 200;
const FLOOR_WIRE_Y = 100;
const WALL_WIRE_BASE_Y = 70;

const TYPE_SLOT = 'slot';
const TYPE_CIRCUIT = 'circuit';

const CONNECTION_IN_OUT_DIST = 10; // Distance between incoming & outgoing connections at the bottom of an element

const DEFAULT_ALIGN = { x: 0.5, y: 0.5 };
const DEFAULT_OFFSET = { x: 0, y: 0 };

let tempConnectionLoc;

function idToString(id) {
  const t = typeof (id);
  if (t === 'string') {
    return id;
  }
  if (t === 'number') {
    return id.toString(16);
  }
  return '';
}

export function getMixedCircuitWireDistance() {
  return MIXED_CIRCUIT_WIRE_DISTANCE;
}

export function elementIsOnFloor(element) {
  if (!element) {
    return false;
  }
  const { data, productInfo } = element;

  if (productInfo) {
    const { parameters } = productInfo;
    if (parameters && typeof (parameters.placement) === 'string') {
      const placement = parameters.placement.toLowerCase();
      return placement === 'floor' || placement === 'vloer';
    }
  }

  if (data && typeof (data.onFloor) === 'boolean') {
    return data.onFloor;
  }

  const { displayInfo } = element;
  if (!displayInfo) {
    return false;
  }
  return displayInfo.onFloor === true;
}

export function getDisplayInfoOffset(displayInfo, onFloor) {
  if (!displayInfo) {
    return null;
  }
  const isOnFloor = typeof (onFloor) === 'boolean' ? onFloor : displayInfo.onFloor;

  const { offset, wallOffset, floorOffset } = displayInfo;

  const result = isOnFloor ? floorOffset : wallOffset;
  if (result) {
    return result;
  }
  return offset || DEFAULT_OFFSET;
}

export function getDisplayInfoAlign(displayInfo, onFloor) {
  if (!displayInfo) {
    return null;
  }
  const isOnFloor = typeof (onFloor) === 'boolean' ? onFloor : displayInfo.onFloor;

  const { align, wallAlign, floorAlign } = displayInfo;

  const result = isOnFloor ? floorAlign : wallAlign;
  if (result) {
    return result;
  }
  return align || DEFAULT_ALIGN;
}

export function getElementOffset(element) {
  if (!element) {
    return null;
  }
  if (element.offset) {
    return element.offset;
  }
  const { displayInfo } = element;
  if (!displayInfo) {
    return null;
  }
  const onFloor = elementIsOnFloor(element);

  return getDisplayInfoOffset(displayInfo, onFloor);
}

export function getElementAlign(element, result = null) {
  if (!element) {
    return null;
  }

  if (element.align) {
    return element.align;
  }

  if (element.type === 'solar-kit' && element.solarCollectorType && element.solarCollectorType.align) {
    return element.solarCollectorType.align;
  }

  const { displayInfo } = element;
  if (!displayInfo) {
    return null;
  }
  const onFloor = elementIsOnFloor(element);
  return getDisplayInfoAlign(displayInfo, onFloor, -1, -1, result);
}

export function getDisplayInfoRectangle(displayInfo, onFloor, width = -1, height = -1, result = null) {
  let wdth = width;
  let hth = height;
  let x = 0;
  let y = 0;
  const res = result || {};
  if (displayInfo) {
    if (wdth < 0 || typeof (wdth) !== 'number') {
      wdth = displayInfo.width;
    }
    if (hth < 0 || typeof (hth) !== 'number') {
      hth = displayInfo.height;
    }
  }
  const align = getDisplayInfoAlign(displayInfo, onFloor);
  const offset = getDisplayInfoOffset(displayInfo, onFloor);

  let ax = 0.5;
  let ay = 0.5;
  let ox = 0;
  let oy = 0;

  if (align) {
    if (typeof (align.x) === 'number') {
      ax = align.x;
    }
    if (typeof (align.y) === 'number') {
      ay = align.y;
    }
  }
  if (offset) {
    if (typeof (offset.x) === 'number') {
      ox = offset.x;
    }
    if (typeof (offset.y) === 'number') {
      oy = offset.y;
    }
  }

  x = ox - (wdth * ax);
  y = oy + (hth * ay);

  res.x = x;
  res.y = y;
  res.width = wdth;
  res.height = hth;
  return res;
}

export function getElementRectangle(element, result = null) {
  if (!element) {
    if (result) {
      const r = result;
      r.x = 0;
      r.y = 0;
      r.width = 0;
      r.height = 0;
    }
    return result;
  }
  const { displayInfo, width, height } = element;
  const onFloor = elementIsOnFloor(element);
  const res = result || {};
  getDisplayInfoRectangle(displayInfo, onFloor, width, height, res);

  return res;
}

function getProductInfoByElement(element, config, options) {
  if (!element || !options) {
    return null;
  }
  const { products } = options;
  if (!products) {
    return null;
  }
  const slotName = element.name;
  return getProductInfoBySlotName(slotName, config, products);
}

// TODO: remove function below
export function getProductInfoByElementOld(element, config, options) {
  if (!element || !options) {
    return null;
  }
  const { products } = options;
  if (!products) {
    return null;
  }
  const { data } = element;
  const dataId = `${data.id}`;

  const slotName = element.name;

  const productMap = products.attributes.sets;
  const numSets = Object.keys(productMap).length;

  // Exceptions, exceptions
  if (slotName === 'solar-kit') { // Solar kit
    const solarKitData = findSolarKitProductData(slotName, config, products);
    if (!solarKitData) {
      return null;
    }
    return solarKitData['solar-kit'];
    //
  } else if (slotName && slotName.startsWith('hydraulics')) { // Hydraulics
    for (let i = 0; i < numSets; i += 1) {
      const name = `hydraulics-${i + 1}`;
      const array = productMap[name];
      if (array) {
        for (let j = 0, length = array.length; j < length; j += 1) {
          const hydraulicsProduct = array[j];
          const hydraulicsProductID = `${hydraulicsProduct.id}`;
          if (hydraulicsProductID === dataId) {
            return hydraulicsProduct;
          }
        }
      }
    }
    const hydraulicsArray = products.attributes.hydraulics;
    const num = hydraulicsArray ? hydraulicsArray.length : 0;
    for (let i = 0; i < num; i += 1) {
      const hydraulicsProduct = hydraulicsArray[i];
      const hydraulicsProductID = `${hydraulicsProduct.id}`;
      if (hydraulicsProductID === dataId) {
        return hydraulicsProduct;
      }
    }
  } else if (slotName && slotName.startsWith('control')) {
    // TODO: should merge this function with utils/project-definition/component/controls/index ->  findControlProduct
    const { property } = data;
    for (let i = 0; i < 10; i += 1) {
      const controlSetName = `control-${i + 1}`;
      // If property (control slot in product sets) is not set, check all control sets
      const controlProducts = getSystemProjectSet(controlSetName, products);
      const numControlProducts = controlProducts ? controlProducts.length : 0;
      for (let j = 0; j < numControlProducts; j += 1) {
        const controlProduct = controlProducts[j];
        const controlProductId = `${controlProduct.id}`;
        const controlProductSetId = controlProduct.set_identifier || controlProduct.set_id;
        if (controlProductId === dataId && controlProductSetId === property) {
          return controlProduct;
        }
      }
    }
    return null;
  }

  const productInfoArray = getSystemProjectSet(element.name, products);

  if (!productInfoArray) {
    return null;
  }
  const numProducts = productInfoArray.length;
  for (let i = 0; i < numProducts; i += 1) {
    const productInfo = productInfoArray[i];
    const productInfoId = `${productInfo.id}`;
    if (dataId === productInfoId) {
      return productInfo;
    }
  }
  return null;
}

function getDisplayInfoByTypeAndId(type, itemName, id) {
  let dispInf = null;
  if (!dispInf) {
    const typeDispInf = configuratorComponentInfo[type];
    const itemDispInf = typeDispInf ? typeDispInf[itemName] : null;
    dispInf = null;
    if (itemDispInf) {
      if (id && itemDispInf.ids) {
        dispInf = itemDispInf.ids[id];
      }
      if (!dispInf) {
        dispInf = itemDispInf.def;
      }
    }
  }
  return dispInf;
}

function newElement(object, type, name, dInf, config, options) {
  if (!object) {
    return null;
  }
  let res = null;

  if (options) {
    // Try to reuse some stuff
    const { mapping } = options;

    if (mapping && mapping.get) {
      res = mapping.get(object);
    }
  }
  if (!res) {
    res = {};
  }
  const id = idToString(object.id);
  let dispInf = dInf;
  if (!dispInf) {
    dispInf = getDisplayInfoByTypeAndId(type, name, id);
  }
  /*
  if (!dispInf) {
    const typeDispInf = configuratorComponentInfo[type];
    const itemDispInf = typeDispInf ? typeDispInf[name] : null;
    dispInf = null;
    if (itemDispInf) {
      if (id && itemDispInf.ids) {

        dispInf = itemDispInf.ids[id];
      }
      if (!dispInf) {
        dispInf = itemDispInf.def;
      }
    }
  }
  */

  res.displayInfo = dispInf;
  res.data = object;
  res.type = name;
  res.name = name;
  res.x = 0;
  res.y = 0;

  res.productInfo = getProductInfoByElement(res, config, options);

  if (options) {
    const { mapping } = options;
    if (mapping && mapping.set) {
      mapping.set(object, res);
    }
  }
  return res;
}

function newSlotElement(name, slots, config, options) {
  if (!slots || !name) {
    return null;
  }
  let object = slots[name];
  if (!object) {
    const aliasList = aliasMap[name];
    const num = aliasList ? aliasList.length : 0;
    for (let i = 0; i < num; i += 1) {
      const o = slots[aliasList[i]];
      if (o) {
        object = o;
        break;
      }
    }
  }

  return newElement(object, TYPE_SLOT, name, null, config, options);
}

function setElementPos(element, xpos, ypos) {
  if (!element) {
    return;
  }
  const e = element;
  e.x = xpos || 0;
  e.y = ypos || 0;
}

function getElementResultY(element) {
  if (!element) {
    return 0;
  }
  const onFloor = elementIsOnFloor(element);
  if (onFloor) {
    return 0;
  }
  const dispInf = element.displayInfo;
  const height = dispInf ? dispInf.height || 0 : 0;

  const align = getElementAlign(element);
  const alignY = align ? align.y || 0 : 0;

  return WALL_Y + (height * (1 - alignY));
}

function isSmallLayout(options) {
  if (options) {
    return !!options.smallLayout;
  }
  return true;
}

function isSingleWire(options) {
  if (options && typeof (options.singleWire) === 'boolean') {
    return options.singleWire;
  }
  return isSmallLayout(options);
}

function getSingleWireColor(options) {
  const fallback = 'gray';
  if (!options) {
    return fallback;
  }
  if (options.singleWireColor) {
    return options.singleWireColor;
  }
  return fallback;
}

function getColor1(options) {
  if (isSingleWire(options)) {
    return getSingleWireColor(options);
  }
  const fallback = 'red';
  return options ? options.color1 || fallback : fallback;
}

function getColor2(options) {
  if (isSingleWire(options)) {
    return getSingleWireColor(options);
  }
  const fallback = 'blue';
  return options ? options.color2 || fallback : fallback;
}

function expandBoundingBoxPoint(x, y, bounds) {
  if (!bounds) {
    return;
  }
  const b = bounds;
  if (typeof (b.minx) !== 'number') {
    b.minx = x;
  } else {
    b.minx = x < b.minx ? x : b.minx;
  }
  if (typeof (b.maxx) !== 'number') {
    b.maxx = x;
  } else {
    b.maxx = x > b.maxx ? x : b.maxx;
  }

  if (typeof (b.miny) !== 'number') {
    b.miny = y;
  } else {
    b.miny = y < b.miny ? y : b.miny;
  }
  if (typeof (b.maxy) !== 'number') {
    b.maxy = y;
  } else {
    b.maxy = y > b.maxy ? y : b.maxy;
  }
}
function expandBoundingBox(element, bounds) {
  if (!element || !bounds) {
    return;
  }
  const dispInf = element.displayInfo;
  let width = 0;
  let height = 0;
  if (dispInf) {
    width = dispInf.width;
    height = dispInf.height;
  }
  if (typeof (element.width) === 'number') {
    width = element.width;
  }
  if (typeof (element.height) === 'number') {
    height = element.height;
  }
  const a = getElementAlign(element);
  const ax = a ? a.x || 0 : 0;
  const ay = a ? a.y || 0 : 0;
  const xpos = element.x || 0;
  const ypos = element.y || 0;
  const left = xpos - (width * ax);
  const top = ypos + (height * ay);
  const right = left + width;
  const bottom = top - height;
  const b = bounds;

  if (typeof (b.minx) !== 'number') {
    b.minx = left;
  } else {
    b.minx = left < b.minx ? left : b.minx;
  }
  if (typeof (b.maxx) !== 'number') {
    b.maxx = right;
  } else {
    b.maxx = right > b.maxx ? right : b.maxx;
  }

  if (typeof (b.miny) !== 'number') {
    b.miny = bottom;
  } else {
    b.miny = bottom < b.miny ? bottom : b.miny;
  }
  if (typeof (b.maxy) !== 'number') {
    b.maxy = top;
  } else {
    b.maxy = top > b.maxy ? top : b.maxy;
  }
}

function getConnectionInsetX(options) {
  if (options && typeof (options.connectionInsetX) === 'number') {
    return options.connectionInsetX;
  }
  return 0;
}

function getConnectionInsetY(options) {
  if (options && typeof (options.connectionInsetY) === 'number') {
    return options.connectionInsetY;
  }
  return 0;
}

/**
 * @function getElementConnectionLocation
 * @description Returns the absolute location of a connection point of an element
 * @param {Object} element
 * @param {String} connectionType - which connection should be returned
 *  - left: incoming connection at the left, when placed on the floor
 *  - right: outgoing connection at the right, when placed on the floor
 *  - bottomleft: incoming connection at the bottom left, when placed on a wall
 *  - bottomright: outgoing connection at the bottom right, when placed on a wall
 *  - bottom: connection at the bottom center
 *  - autoleft: same as left or bottomleft, whether the element is placed on the floor
 *      (if the element is placed on the floor, the 'left' connection is returned, else 'bottomleft' is returned)
 *  - autoright: same as right or bottomright, whether the element is placed on the floor
 *      (if the element is placed on the floor, the 'right' connection is returned, else 'bottomright is returned')
 * @param {Object} options - optional options object
 * @param {Object} outLoc - optional preallocated object where the result will be stored
 * @return {Object} - object containing x and y values - Same object as outLoc if not null
 * */
function getElementConnectionLocation(element, connectionType, options, outLoc) {
  if (!element) {
    return outLoc;
  }
  const dispInf = element.displayInfo;
  let width = 0;
  let height = 0;
  if (dispInf) {
    width = dispInf.width;
    height = dispInf.height;
  }
  let connType = connectionType;
  const { x, y } = element;
  const align = getElementAlign(element);

  let alignX = 0.5;
  let alignY = 0.5;

  if (align) {
    if (typeof (align.x) === 'number') {
      alignX = align.x;
    }
    if (typeof (align.y) === 'number') {
      alignY = align.y;
    }
  }

  const onFloor = elementIsOnFloor(element);
  let resX = x;
  let resY = y;

  if (connType === 'autoleft') {
    connType = onFloor ? 'left' : 'bottomleft';
  } else if (connType === 'autoright') {
    connType = onFloor ? 'right' : 'bottomright';
  }

  if (
    onFloor &&
    (
      connType === 'left' || connType === 'right'
    )
  ) {
    resY = FLOOR_WIRE_Y;
  }

  const insetX = getConnectionInsetX(options);
  const insetY = getConnectionInsetY(options);

  if (connType === 'left') {
    resX = x - ((width * alignX) * insetX);
  } else if (connType === 'right') {
    resX = x + (width * (1 - alignX) * insetX);
  } else {
    const isBottom = connType === 'bottom';
    const isBottomLeft = connType === 'bottomleft';
    const isBottomRight = connType === 'bottomright';

    const isTop = connType === 'top';
    const isTopLeft = connType === 'topleft';
    const isTopRight = connType === 'topright';

    const connDist = CONNECTION_IN_OUT_DIST;

    if (isBottom || isBottomLeft || isBottomRight) {
      resY = y - (height * (1 - alignY) * insetY);
    } else if (isTop || isTopLeft || isTopRight) {
      // resY = y + (height * (alignY) * insetY);
      resY = y;
    }

    if (isBottomLeft || isTopLeft) {
      resX -= connDist;
    } else if (isBottomRight || isTopRight) {
      resX += connDist;
    }
  }

  const loc = outLoc || {};

  loc.x = resX;
  loc.y = resY;
  loc.type = connType;

  return loc;
}

function createConnectionWire(x1, y1, x2, y2, baseY, inverted, color, fromData, toData, options, result = null) {
  if (result) {
    const res = result;
    res.x1 = x1;
    res.y1 = y1;
    res.x2 = x2;
    res.y2 = y2;
    res.baseY = baseY;
    res.inverted = inverted;
    res.color = color;
    if (fromData) {
      res.fromData = fromData;
    }
    if (toData) {
      res.toData = toData;
    }
    return res;
  }
  return { x1, y1, x2, y2, baseY, inverted, color };
}

function connectWires(x1, y1, x2, y2, baseY, inverted, color, fromData, toData, options, result) {
  const conn = createConnectionWire(x1, y1, x2, y2, baseY, inverted, color, fromData, toData, options);
  if (!conn) {
    return result;
  }

  const res = result || [];

  res.push(conn);

  return res;
}

function connectWiresBetweenElements(elem1, elem2, inverted, options, result) {
  let w1x1 = elem1.x;
  let w1y1 = elem1.y;
  let w1x2 = elem2.x;
  let w1y2 = elem2.y;

  let w2x1 = elem1.x;
  let w2y1 = elem1.y;
  let w2x2 = elem2.x;
  let w2y2 = elem2.y;

  let baseY1 = null;
  let baseY2 = null;

  let res = result || [];

  const color1 = getColor1(options);
  const color2 = getColor2(options);

  const onFloor1 = elementIsOnFloor(elem1);
  const onFloor2 = elementIsOnFloor(elem2);
  let floorState = 0;
  if (onFloor1) {
    floorState += 1;
  }
  if (onFloor2) {
    floorState += 2;
  }
  // floorState === 0 -> both elements on the wall
  // floorState === 1 -> element 1 on the floor, element 2 on the wall
  // floorState === 2 -> element 1 on the wall, element 2 on the floor
  // floorState === 3 -> both elements on the floor

  if (floorState === 0) {
    const baseY = WALL_WIRE_BASE_Y;
    baseY1 = baseY;
    baseY2 = baseY;
  }

  const tempConnLoc = tempConnectionLoc || {};
  tempConnectionLoc = tempConnLoc;

  // getElementConnectionLocation(curElem, 'autoright', options, tempConnLoc);
  getElementConnectionLocation(elem1, 'autoright', options, tempConnLoc);
  w1x1 = tempConnLoc.x;
  w1y1 = tempConnLoc.y;
  w2x1 = tempConnLoc.x;
  w2y1 = tempConnLoc.y;

  getElementConnectionLocation(elem2, 'autoleft', options, tempConnLoc);
  w1x2 = tempConnLoc.x;
  w1y2 = tempConnLoc.y;
  w2x2 = tempConnLoc.x;
  w2y2 = tempConnLoc.y;

  const singleWire = isSingleWire(options);

  const wDist = singleWire ? 0 : WIRE_SPACE * 0.5;

  if (floorState === 0) {
    // both elements on the wall
    w1x1 += wDist;
    w2x1 -= wDist;

    w1x2 -= wDist;
    w2x2 += wDist;

    baseY1 = WALL_WIRE_BASE_Y;
    baseY2 = WALL_WIRE_BASE_Y;
    baseY1 += wDist * 0.5;
    baseY2 -= wDist * 0.5;
  } else if (floorState === 1) {
    // element 1 on the floor, element 2 on the wall
    w1x2 -= wDist;
    w2x2 += wDist;

    w1y1 += wDist;
    w2y1 -= wDist;
  } else if (floorState === 2) {
    // element 1 on the wall, element 2 on the floor
    w1x1 += wDist;
    w2x1 -= wDist;

    w1y2 += wDist;
    w2y2 -= wDist;
  } else if (floorState === 3) {
    // both elements on the floor
    let avg;
    avg = Math.floor((w1y1 + w1y2) * 0.5);
    w1y1 = avg;
    w1y2 = avg;

    avg = Math.floor((w2y1 + w2y2) * 0.5);
    w2y1 = avg;
    w2y2 = avg;

    w1y1 += wDist;
    w1y2 += wDist;

    w2y1 -= wDist;
    w2y2 -= wDist;
  }

  res = connectWires(w1x1, w1y1, w1x2, w1y2, baseY1, inverted, color1, elem1, elem2, options, res);
  if (!singleWire) {
    res = connectWires(w2x1, w2y1, w2x2, w2y2, baseY2, inverted, color2, elem1, elem2, options, res);
  }
  return res;
}

function layoutElement(element, location, config, options, output) {
  if (!element) {
    return;
  }
  const loc = location;
  const { x, y } = loc;
  setElementPos(element, x, y, options);
  output.push(element);
}

// Converts {type: ..., amount: N, ...} to
// {type: ...}, {type: ...}, {type: ...}, ... N
function expandCircuits(circuits) {
  if (!circuits) {
    return circuits;
  }
  const num = circuits.length;
  let res = circuits;
  for (let i = 0; i < num; i += 1) {
    const circuit = circuits[i];
    if (circuit && circuit.amount !== 1) {
      if (res === circuits) {
        res = circuits.slice(0, i);
      }
      const amount = circuit.amount;
      for (let j = 0; j < amount; j += 1) {
        res.push({
          type: circuit.type,
          location: circuit.location,
        });
      }
    }
  }
  return res;
}

function getSolarCollectorType(solarCollector, config, options) { // eslint-disable-line
  let { solar_placement } = config.attributes;

  solar_placement = solar_placement ? solar_placement.toLowerCase() : null;
  if (solar_placement === 'on') {
    return SOLAR_COLLECTOR_TYPES.ON_ROOF;
  } else if (solar_placement === 'in') {
    return SOLAR_COLLECTOR_TYPES.IN_ROOF;
  } else if (solar_placement === 'flat') {
    return SOLAR_COLLECTOR_TYPES.HORIZONTAL_ROOF;
  }
  return SOLAR_COLLECTOR_TYPES.IN_ROOF;
}


function expandBoundingBoxConnection(conn, bounds) {
  const { x1, y1, x2, y2, baseY } = conn;
  expandBoundingBoxPoint(x1, y1, bounds);
  expandBoundingBoxPoint(x2, y2, bounds);
  if (baseY !== null && typeof baseY === 'number' && !isNaN(baseY)) {
    expandBoundingBoxPoint(x1, baseY, bounds);
    expandBoundingBoxPoint(x2, baseY, bounds);
  }
}

export function computeLayoutBounds(layout) {
  if (!layout) {
    return;
  }
  const lyt = layout;
  const bounds = layout.bounds || {};
  lyt.bounds = bounds;

  const { elements, connections } = lyt;

  const numElements = elements ? elements.length : 0;
  for (let i = 0; i < numElements; i += 1) {
    const elem = elements[i];
    expandBoundingBox(elem, bounds);
  }

  const numConn = connections ? connections.length : 0;
  for (let i = 0; i < numConn; i += 1) {
    const conn = connections[i];
    if (conn) {
      expandBoundingBoxConnection(conn, bounds);
    }
  }
  if (bounds) {
    bounds.width = bounds.maxx - bounds.minx;
    bounds.height = bounds.maxy - bounds.miny;
  }
}

function kettleHasExhaust(kettleData) {
  if (!kettleData) {
    return false;
  }

  const { productInfo } = kettleData;
  if (!productInfo) {
    return false;
  }

  const exhaustLayouts = productInfo.exhaust_layouts;
  if (exhaustLayouts && exhaustLayouts.length > 0) {
    return true;
  }

  const { chimneys } = productInfo;
  if (chimneys && chimneys.length > 0) {
    return true;
  }

  return false;
}

// Converts a config object to an object containing the layout of the elements & connections
// Note:
//  x-coordinates go from left to right,
//  y-coordinates go from bottom to top, where the floor is 0
//  Conversion to pixel coordinates: outputY = FLOOR_Y_CONST - inputY;
export function createConfigLayout(config, options = null, result = null) {
  if (!config) {
    return null;
  }
  let errors = null;
  if (options) {
    errors = options.errors;
    if (errors) {
      errors.length = 0;
    }
  }

  let tempConnLoc = tempConnectionLoc || {};
  tempConnectionLoc = tempConnLoc;

  const singleWire = isSingleWire(options);

  const attribs = config.attributes;
  if (!attribs) {
    return null;
  }
  const { slots } = attribs;
  let { circuits } = attribs;

  const slotKeys = Object.keys(slots);
  const numSlots = slotKeys.length;

  circuits = expandCircuits(circuits);

  let extUnit = null;
  const extUnit2 = null; // Remove external unit 2? Not sure if there can be two in one system
  let pump = null;
  let kettle = null;
  let exhaust = null;
  let controls = null;
  let boiler = null;
  let solarCollector = null;

  const res = result || {};

  const elementsArr = res.elements || [];
  const connections = res.connections || [];

  elementsArr.length = 0;
  connections.length = 0;

  res.elements = elementsArr;
  res.connections = connections;

  const color1 = getColor1(options);
  const color2 = getColor2(options);

  let numControls = 0;
  let numCircuitControls = 0; // number of controls on a circuit (not in kettle or somewhere else)

  if (slots) {
    extUnit = null;

    pump = newSlotElement('pump', slots, config, options);
    kettle = newSlotElement('kettle', slots, config, options);
    exhaust = newSlotElement('exhaust', slots, config, options);

    if (attribs.has_chimney && kettleHasExhaust(kettle)) {
      if (kettle.data && !getChimneyLayout('kettle', config)) {
        // set current exhaust to kettle property
        const exhaustLayouts = kettle && kettle.productInfo ? kettle.productInfo.exhaust_layouts : null;
        let exhaustLayout = null;
        if (exhaustLayouts) {
          if (exhaustLayouts.indexOf('C33', 0) >= 0) {
            exhaustLayout = 'C33';
          } else {
            exhaustLayout = exhaustLayouts[0];
          }
          kettle.data.property = exhaustLayout;
        }
      }

      if (!exhaust) {
        exhaust = newElement({}, 'slot', 'exhaust', null, config, options);
      }
    }

    // Find external unit
    const externalPump = slots.external_pump;
    if (externalPump) {
      // External pump as separate configurable product (Remeha NL)
      const externalPumpProduct = getProductInfoBySlotName('external_pump', config, options && options.products);
      if (externalPumpProduct) {
        extUnit = {
          name: 'external_pump',
          type: 'external_pump',
          data: externalPump,
          displayInfo: getDisplayInfoByTypeAndId('slot', 'external_pump'),
          productInfo: externalPumpProduct,
        };
      }
    } else if (externalPump !== false) {
      // External pump is probably undefined, fallback to external unit of the kettle or pump (Remeha BE)
      let externalUnitProductInfo = null;
      if (kettle && kettle.productInfo && kettle.productInfo.parameters && kettle.productInfo.parameters.external_unit) {
        externalUnitProductInfo = kettle.productInfo.parameters.external_unit;
      } else if (pump && pump.productInfo && pump.productInfo.parameters && pump.productInfo.parameters.external_unit) {
        externalUnitProductInfo = pump.productInfo.parameters.external_unit;
      }

      if (externalUnitProductInfo) {
        extUnit = {
          name: 'external_unit',
          type: 'external_unit',
          displayInfo: getDisplayInfoByTypeAndId('slot', 'external_unit'),
          productInfo: externalUnitProductInfo,
        };
      }
    }

    const maxControls = MAX_CONTROLS;
    for (let i = 0; i < maxControls; i += 1) {
      if (!controls) {
        controls = [];
        controls.length = maxControls;
      }
      const contrElem = newSlotElement(`controls-${i + 1}`, slots, config, options);
      if (contrElem && contrElem.productInfo) {
        const controlProduct = contrElem.productInfo;
        const controlPlacement = controlProduct.control_placement;
        if (
          controlPlacement === 'control' ||
          controlPlacement === 'controls' ||
          controlPlacement === 'circuit'
        ) {
          numCircuitControls += 1;
        }

        contrElem.type = 'control';
        controls[numControls] = contrElem;
        numControls += 1;
      }
    }

    boiler = newSlotElement('boiler', slots, config, options);

    solarCollector = newSlotElement('solar-kit', slots, config, options);
    if (solarCollector) {
      const solarCollectorType = getSolarCollectorType(solarCollector, config, options);
      solarCollector.solarCollectorType = solarCollectorType;
      solarCollector.align = solarCollectorType ? solarCollectorType.align || solarCollector.align : solarCollector.align;

      if (solarCollectorType && solarCollectorType.align) {
        const { align } = solarCollectorType;
        solarCollector.displayInfo.align = { x: align.x, y: align.y };
      }
    }
  }
  if (controls) {
    controls.length = numControls;
  }
  const location = { x: 0, y: 0 };

  // external unit, pump & kettle
  if (extUnit) {
    location.y = getElementResultY(extUnit);
    layoutElement(extUnit, location, config, options, elementsArr);

    location.x += 300;
  }

  if (extUnit2) {
    location.y = getElementResultY(extUnit2);
    layoutElement(extUnit2, location, config, options, elementsArr);

    location.x += 300;
  }

  if (pump) {
    location.y = getElementResultY(pump);
    layoutElement(pump, location, config, options, elementsArr);
    location.y = 0;
    location.x += 280;
  }

  if (kettle) {
    const kettleHeight = kettle.displayInfo.height;
    const curAlign = getElementAlign(kettle);

    const alignY = curAlign ? curAlign.y || 0 : 0;
    const kettleY = getElementResultY(kettle);

    if (exhaust) {
      const exhaustY = kettleY + (kettleHeight * alignY);
      location.y = exhaustY;
      layoutElement(exhaust, location, config, options, elementsArr);
    }
    location.y = kettleY;
    layoutElement(kettle, location, config, options, elementsArr);
    location.x += 220;
  }
  const xpos = location.x;
  const control_space_x = 200;
  const control_x = xpos;

  // control boxes
  location.y = CONTROLBOX_CENTER_Y;
  const numCircuits = circuits ? circuits.length : 0;
  let numVisibleCircuits = numCircuits;
  let ctrlBoxElements = null;
  let circuitElements = null;

  let lastCircuitElem = null;

  let control_x_space_scale;
  const occupiedByHydraulicsCircuits = [];

  let lastCircuitX = location.x;

  if (numCircuits > 0) {
    control_x_space_scale = [];
    ctrlBoxElements = [];
    ctrlBoxElements.length = numCircuits;
    circuitElements = [];
    circuitElements.length = numCircuits;

    const hydraulicsByCircuit = {};
    // Collect hydraulics
    const MAX_HYDRAULICS = numSlots;
    for (let i = 0; i < MAX_HYDRAULICS; i += 1) {
      const hIndex = i + 1;
      let name = `hydraulics_${hIndex}`;
      let contrBox = newSlotElement(name, slots, config, options);
      if (!contrBox) {
        name = `hydraulics-${hIndex}`;
        contrBox = newSlotElement(name, slots, config, options);
      }
      if (contrBox) {
        contrBox.type = 'hydraulics';
        const hydraulicsProduct = contrBox.productInfo;
        // #if DEBUG
        if (!hydraulicsProduct) {
          console.warn(`No product found for ${name}`, { config, options });
        }
        // #endif
        const hydraulicsCircuit = hydraulicsProduct ? hydraulicsProduct.circuit : null;
        if (hydraulicsCircuit) {
          const hydraulicsArray = hydraulicsByCircuit[hydraulicsCircuit] || [];
          hydraulicsByCircuit[hydraulicsCircuit] = hydraulicsArray;
          hydraulicsArray.push(contrBox);
        }
      }
    }

    let circuitIndex = 0;
    lastCircuitX = location.x;

    for (let i = 0; i < numCircuits; i += 1) {
      const circuit = circuits[i];
      const occupiedByHydraulics = occupiedByHydraulicsCircuits[i];
      const circuitTypeName = circuit ? circuit.type : null;
      const circuitType = CIRCUIT_TYPES_BY_NAME[circuitTypeName];

      const circuitDisplayInfoName = circuitType && circuitType.direct ? 'direct' : 'mixed';
      const circuitDispInf = circuitType ? circuitDisplayInfo[circuitDisplayInfoName] : null;
      if (circuitDispInf) {
        let contrBox = null;
        let ctlXSpace = control_space_x;
        if (circuitType.direct && circuitType.extern) {
          let loop = true;
          for (let j = i + 1; loop && j < numCircuits; j += 1) {
            const nextCircuit = circuits[j];
            const nextCircuitTypeName = nextCircuit ? nextCircuit.type : null;
            const nextCircuitType = CIRCUIT_TYPES_BY_NAME[nextCircuitTypeName];
            if (nextCircuitType && nextCircuitType.extern) {
              if (!nextCircuitType.direct) {
                if (hydraulicsByCircuit && hydraulicsByCircuit.double && hydraulicsByCircuit.double.length > 0) {
                  occupiedByHydraulicsCircuits[j] = true;
                  contrBox = hydraulicsByCircuit.double.shift();
                  contrBox.displayInfo.width = doubleControlboxComponentInfo.width;
                  contrBox.displayInfo.height = doubleControlboxComponentInfo.height;
                  contrBox.double = true;

                  const scaleX = 0.6;
                  ctlXSpace = control_space_x * scaleX;
                  control_x_space_scale[circuitIndex] = scaleX;
                }
              }
              loop = false;
            }
          }
        }

        if (!contrBox && circuitType && circuitType.extern && !occupiedByHydraulics) {
          // Only render hydraulics on an external circuits
          const hydraulicsCircuitName = circuitType && circuitType.direct ? 'direct' : 'gemengd';
          const hydraulicsArray = hydraulicsByCircuit[hydraulicsCircuitName];
          contrBox = hydraulicsArray ? hydraulicsArray.shift() : null;
        }
        if (contrBox) {
          occupiedByHydraulicsCircuits[i] = true;
        }

        const circuitElem = newElement(circuit, TYPE_CIRCUIT, `circuit-${i + 1}`, circuitDispInf, config, options);
        circuitElem.type = 'circuit';
        circuitElem.circuitType = circuitType;
        layoutElement(circuitElem, location, config, options, elementsArr);
        circuitElem.y = CIRCUIT_CENTER_Y;

        lastCircuitElem = circuitElem;
        circuitElements[circuitIndex] = circuitElem;

        ctrlBoxElements[circuitIndex] = contrBox;

        layoutElement(contrBox, location, config, options, elementsArr);
        if (contrBox && contrBox.double) {
          contrBox.x += 65;
        }

        lastCircuitX = location.x;

        location.x += ctlXSpace;

        circuitIndex += 1;
      }
    }
    numVisibleCircuits = circuitIndex;
  }
  const controlboxesRight = location.x;

  // controls

  location.x = control_x;
  location.y = CONTROL_CENTER_Y;
  let controlsRight1 = control_x;
  let controlsRight2 = control_x;
  let ctlIndex = 0;

  if (controls && numControls > 0) {
    // first row
    for (let i = 0; i < numControls; i += 1) {
      const contr = controls[i];
      if (contr) {
        const controlProductInfo = contr.productInfo;
        const controlPlacement = controlProductInfo ? controlProductInfo.control_placement : null;
        if (!controlPlacement || controlPlacement === 'control' || controlPlacement === 'controls') {
          layoutElement(contr, location, config, options, elementsArr);
          const scale = typeof (control_x_space_scale[ctlIndex]) === 'number' ? control_x_space_scale[ctlIndex] : 1;
          location.x += control_space_x * scale;
          ctlIndex += 1;
        }
      }
    }
    controlsRight1 = location.x;

    // second row
    location.x = control_x;
    location.y = CONTROL_CENTER_Y - 70;

    for (let i = 0; i < numControls; i += 1) {
      const contr = controls[i];
      if (contr) {
        const controlProductInfo = contr.productInfo;
        const controlPlacement = controlProductInfo ? controlProductInfo.control_placement : null;
        if (controlPlacement === 'kettle') {
          layoutElement(contr, location, config, options, elementsArr);
          const scale = typeof (control_x_space_scale[ctlIndex]) === 'number' ? control_x_space_scale[ctlIndex] : 1;
          location.x += control_space_x * scale;
          ctlIndex += 1;
        }
      }
    }
    controlsRight2 = location.x;
  }
  const controlsRight = controlsRight1 > controlsRight2 ? controlsRight1 : controlsRight2;
  location.x = xpos;

  location.x = (controlboxesRight > controlsRight) ? controlboxesRight : controlsRight;

  // solar collector ?
  if (solarCollector) {
    location.y = SOLAR_COLLECTOR_Y;
    layoutElement(solarCollector, location, config, options, elementsArr);
  }

  // boiler / solar boiler
  if (boiler) {
    const align = getElementAlign(boiler);

    if (elementIsOnFloor(boiler)) {
      location.y = 0;
    } else {
      location.y = WALL_Y + (boiler.displayInfo.height * (1 - align.y) * getConnectionInsetY(options));
    }
    layoutElement(boiler, location, config, options, elementsArr);
  }

  if (boiler || solarCollector) {
    const wDist = singleWire ? 0 : WIRE_SPACE * 0.5;

    let solarCollectorType;
    let connectionIn;
    let connectionInOff;
    let connectionInOffX;
    let connectionInOffY;
    let w1off = 0;
    let w2off = 0;
    let w1offX = 0;
    let w1offY = 0;
    let w2offX = 0;
    let w2offY = 0;

    if (solarCollector) {
      solarCollectorType = solarCollector.solarCollectorType;
      connectionIn = solarCollectorType ? solarCollectorType.connectionIn : null;
      connectionInOff = connectionIn ? connectionIn.offset : null;
      connectionInOffX = connectionInOff ? connectionInOff.x || 0 : 0;
      connectionInOffY = connectionInOff ? connectionInOff.y || 0 : 0;

      w1off = connectionIn ? (connectionIn.wire1Offset) : null;
      w2off = connectionIn ? (connectionIn.wire2Offset) : null;

      w1offX = w1off ? (w1off.x || 0) : null;
      w1offY = w1off ? (w1off.y || 0) : null;
      w2offX = w2off ? (w2off.x || 0) : null;
      w2offY = w2off ? (w2off.y || 0) : null;

      if (singleWire || typeof (w1offX) !== 'number' || isNaN(w1offX)) {
        w1offX = 0;
      }
      if (singleWire || typeof (w1offY) !== 'number' || isNaN(w1offY)) {
        w1offY = 0;
      }
      if (singleWire || typeof (w2offX) !== 'number' || isNaN(w2offX)) {
        w2offX = 0;
      }
      if (singleWire || typeof (w2offY) !== 'number' || isNaN(w2offY)) {
        w2offY = 0;
      }
    }

    if (boiler && solarCollector) {
      const boilerDispInfo = boiler.displayInfo;
      const align = getElementAlign(boiler);
      const boilerTop = boiler.y + (boilerDispInfo.height * align.y * getConnectionInsetY(options));

      connectWires(
        boiler.x - wDist,
        boilerTop,
        solarCollector.x + connectionInOffX + w1offX,
        solarCollector.y + connectionInOffY + w1offY,
        null,
        true,
        color1,

        boiler, solarCollector,

        options,
        connections,
      );
      if (!singleWire) {
        connectWires(
          boiler.x + wDist,
          boilerTop,
          solarCollector.x + connectionInOffX + w2offX,
          solarCollector.y + connectionInOffY + w2offY,
          null,
          true,
          color2,

          boiler, solarCollector,

          options,
          connections,
        );
      }
    }
    if (solarCollector && !boiler) {
      const fromX = lastCircuitX;
      const fromY = WALL_WIRE_BASE_Y * 2;
      const solarX = solarCollector.x;

      // horizontal connection
      connectWires(
        fromX + wDist,
        fromY + wDist + 1,
        solarX,
        fromY + wDist + 1,
        null, // base
        false, // inverted
        color1,
        null, null,
        options,
        connections,
      );

      if (!singleWire) {
        connectWires(
          fromX - 2, fromY - wDist + 1, solarX + WIRE_SPACE, fromY - wDist + 1,
          null, // base
          false, // inverted
          color2,
          null, null,
          options,
          connections,
        );
      }

      // connections to solar panel
      connectWires(
        solarX,
        WALL_WIRE_BASE_Y * 2 + wDist,
        solarCollector.x + connectionInOffX + w1offX,
        solarCollector.y + connectionInOffY + w1offY,
        null,
        true,
        color1,

        null, solarCollector,

        options,
        connections,
      );
      if (!singleWire) {
        connectWires(
          solarX + WIRE_SPACE,
          WALL_WIRE_BASE_Y * 2 - wDist,
          solarCollector.x + connectionInOffX + w2offX,
          solarCollector.y + connectionInOffY + w2offY,
          null,
          true,
          color2,

          null, solarCollector,

          options,
          connections,
        );
      }
    }
  }

  const leftElements = [];
  if (extUnit) {
    leftElements.push(extUnit);
  }
  if (extUnit2) {
    leftElements.push(extUnit2);
  }
  if (pump) {
    leftElements.push(pump);
  }
  if (kettle) {
    leftElements.push(kettle);
  }

  const num = leftElements.length;
  for (let i = 1; i < num; i += 1) {
    const e1 = leftElements[i - 1];
    const e2 = leftElements[i];
    connectWiresBetweenElements(e1, e2, false, options, connections);
  }

  // Connection to pump in stead of kettle
  const curElemPump = leftElements.find((element) => {
    return element.type === 'pump';
  });

  // if no pump available take one before the last element
  const curElem = curElemPump || leftElements[num - 1];

  const lastElem = boiler;

  let numCircuitsInbetween = numVisibleCircuits;

  let lastElemOnFloor = false;
  if (curElem) {
    if (lastElem) {
      lastElemOnFloor = elementIsOnFloor(lastElem);
      connectWiresBetweenElements(curElem, lastElem, false, options, connections);
    } else if (lastCircuitElem) {
      numCircuitsInbetween -= 1;
      const wireInv = false;
      const wDist = singleWire ? 0 : (WIRE_SPACE * 0.5);
      const wireBase1 = WALL_WIRE_BASE_Y + (wDist * 0.5);
      const wireBase2 = WALL_WIRE_BASE_Y - (wDist * 0.5);

      tempConnLoc = getElementConnectionLocation(curElem, 'autoright', options, tempConnLoc);
      let o1x = 0;
      let o1y = 0;
      let o2x = 0;
      let o2y = 0;
      if (tempConnLoc.type === 'left' || tempConnLoc.type === 'right') {
        /**
         * If "right" or "left" connection add some vertical space
         * for connection from circuit to last element
         */
        const VERTICAL_WIRE_BUFFER = curElemPump ? WIRE_SPACE * 2 : 0;
        o1y = wDist - VERTICAL_WIRE_BUFFER;
        o2y = -wDist - VERTICAL_WIRE_BUFFER;
      } else if (tempConnLoc.type === 'bottomleft' || tempConnLoc.type === 'bottomright') {
        o1x = wDist;
        o2x = -wDist;
      }
      connectWires(
        tempConnLoc.x + o1x,
        tempConnLoc.y + o1y,
        lastCircuitElem.x + wDist,
        CONTROLBOX_CENTER_Y,
        wireBase1,
        wireInv,
        color1,

        curElem, lastCircuitElem,

        options,
        connections,
      );
      if (!singleWire) {
        connectWires(
          tempConnLoc.x + o2x,
          tempConnLoc.y + o2y,
          lastCircuitElem.x - wDist,
          CONTROLBOX_CENTER_Y,
          wireBase2,
          wireInv,
          color2,

          curElem, lastCircuitElem,

          options,
          connections,
        );
      }
    }
  }

  location.x = control_x;

  const hWireSpace = WIRE_SPACE * 0.5;
  const wDist = singleWire ? 0 : hWireSpace;

  let circuitWireY = FLOOR_WIRE_Y;
  if (!lastElemOnFloor && !elementIsOnFloor(curElem)) {
    circuitWireY = (WALL_Y - WALL_WIRE_BASE_Y) + WIRE_SPACE;
  }
  for (let i = 0; i < numVisibleCircuits; i += 1) {
    const wireX = location.x;
    const wireBase = null;
    const wireInv = false;

    const circuitElem = circuitElements[i];

    const prevCircuitElem = i > 0 ? circuitElements[i - 1] : curElem;

    const circuitData = circuitElem ? circuitElem.data : null;
    if (i < numCircuitsInbetween) {
      connectWires(
        wireX - wDist,
        circuitWireY - wDist,
        wireX - wDist,
        CONTROLBOX_CENTER_Y,
        wireBase,
        wireInv,
        color2,

        prevCircuitElem, circuitElem,

        options,
        connections);
      if (!singleWire) {
        connectWires(
          wireX + wDist,
          circuitWireY + wDist,
          wireX + wDist,
          CONTROLBOX_CENTER_Y,
          wireBase,
          wireInv,
          color1,

          prevCircuitElem, circuitElem,

          options,
          connections);
      }
    }
    let offLeft = 0;
    let offRight = 0;

    if (circuitData.type !== 'direct') {
      const dist = (getMixedCircuitWireDistance() - WIRE_SPACE) * 0.5;
      offLeft = -dist;
      offRight = dist;
    }

    connectWires(
      wireX - wDist,
      CONTROLBOX_CENTER_Y,
      (wireX - hWireSpace) + offLeft,
      CIRCUIT_CENTER_Y,
      wireBase,
      wireInv,
      color2,
      options,

      null, null,

      connections);
    connectWires(
      wireX + wDist,
      CONTROLBOX_CENTER_Y,
      wireX + hWireSpace + offRight,
      CIRCUIT_CENTER_Y,
      wireBase,
      wireInv,
      color1,
      options,

      null, null,

      connections);

    let xSpace = control_space_x;
    if (control_x_space_scale && typeof (control_x_space_scale[i]) === 'number') {
      xSpace *= control_x_space_scale[i];
    }
    location.x += xSpace;
  }

  let scaleX = 1;
  let scaleY = 1;

  if (options) {
    if (options.scaleX) {
      scaleX = options.scaleX;
    } else if (options.scale) {
      scaleX = options.scale;
    }
    if (options.scaleY) {
      scaleY = options.scaleY;
    } else if (options.scale) {
      scaleY = options.scale;
    }
  }
  if (scaleX !== 1 || scaleY !== 1) {
    const numConn = connections ? connections.length : 0;
    for (let i = 0; i < numConn; i += 1) {
      const conn = connections[i];
      conn.x1 *= scaleX;
      conn.y1 *= scaleY;
      conn.x2 *= scaleX;
      conn.y2 *= scaleY;
    }
    const numElements = elementsArr ? elementsArr.length : 0;
    for (let i = 0; i < numElements; i += 1) {
      const elem = elementsArr[i];
      let width = 0;
      let height = 0;
      const dispInf = elem.displayInfo;
      if (dispInf) {
        width = dispInf.width * scaleX;
        height = dispInf.height * scaleY;
      }
      elem.width = width;
      elem.height = height;
      elem.x *= scaleX;
      elem.y *= scaleY;
    }
    res.scaleX = scaleX;
    res.scaleY = scaleY;
  }

  res.numCircuitControls = numCircuitControls;

  // compute boundingbox
  let bounds = options ? options.bounds : null;
  if (!bounds) {
    bounds = res.bounds;
  }
  res.bounds = bounds;

  computeLayoutBounds(res, options);

  const elementsByName = {};
  elementsArr.forEach((elem) => {
    elementsByName[elem.name] = elem;
  });
  res.elementsByName = elementsByName;

  return res;
}



// WEBPACK FOOTER //
// ./js/utils/project-definition/config-layout.js